import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import { TruncatePipe } from './pipe/truncate.pipe';
import { SafeUrl } from './pipe/safeurl.pipe';
import { SafeHtml } from './pipe/safehtml.pipe';

import {AutofocusDirective} from './pipe/auto-focus.directive';
import { EscapeHtmlPipe } from './pipe/html-sanitizer.directive';

import { NgxPaginationModule } from 'ngx-pagination';
import { ShareButtonsModule } from '@ngx-share/buttons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { SharedModule } from './shared.module';

import { HttpBackendClient } from './services/httpbackend-client';

import {
  PerfectScrollbarModule, PerfectScrollbarConfigInterface,
  PERFECT_SCROLLBAR_CONFIG
} from 'ngx-perfect-scrollbar';

import { ApiModule, BASE_PATH } from './rest';
import { environment } from '../environments/environment';

import { MemberModule } from './member/member.module';
import { AuthGuardService } from './services/auth-guard.service';
import { AuthService } from './services/auth.service';
import { HelperService } from './services/helper.service';
import { RedirectGuard } from './services/redirect-guard.service';
import { ScrollHelper } from './services/scroll-helper.service';
import { TokenInterceptor } from './services/token-interceptor';
import { SpaceService } from './services/space.service';
import { SubscribeGuard } from './services/subscribe-guard.service';
import { SubscriptionStatusGuard } from './services/subscriptionstatus-guard.service';

// Payment
import { PaymentService } from './services/payment/payment.service';
import { WorkflowService } from './services/payment/workflow.service';
import { WorkflowGuard } from './services/payment/workflow-guard.service';
import { SubscriptionWorkflowGuard } from './services/subscription/subscription-workflow-guard.service';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './auth/login/login.component';
import { RegisterComponent } from './auth/register/register.component';
import { ForgotPasswordComponent } from './auth/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './auth/reset-password/reset-password.component';
import { HeaderComponent } from './header/header.component';
import { FooterComponent } from './footer/footer.component';
import { LoaderComponent } from './loader/loader.component';
import { VerifyComponent } from './auth/verify/verify.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { TopPageComponent } from './top-page/top-page.component';
import { MainFooterComponent } from './main-footer/main-footer.component';
import { ScenesComponent } from './scenes/scenes.component';
import { ContactComponent } from './contact/contact.component';
import { FaqComponent } from './faq/faq.component';
import { ProductionComponent } from './production/production.component';
import { LanguageComponent } from './language/language.component';
import { LanguageRedirectComponent } from './language-redirect/language-redirect.component';
import { NewsListComponent } from './news-list/news-list.component';
import { NewsDetailComponent } from './news-detail/news-detail.component';
import { PageLoaderComponent } from './page-loader/page-loader.component';
import { PaymentComponent } from './auth/payment/payment.component';
import { TooltipDirective } from './tooltip.directive';
import { SpaceDetailsComponent } from './space-details/space-details.component';
import { VirtualToursComponent } from './space-details/virtual-tours/virtual-tours.component';
import { PageDetailsComponent } from './page-details/page-details.component';
import { PageoverlayLoaderComponent } from './pageoverlay-loader/pageoverlay-loader.component';
import { PlanComponent } from './auth/payment/plan/plan.component';
import { CardComponent } from './auth/payment/card/card.component';
import { ConfirmComponent } from './auth/payment/confirm/confirm.component';
import { AmountComponent } from './auth/payment/amount/amount.component';
import { PlanDetailsComponent } from './auth/payment/amount/plan-details/plan-details.component';
import { SubscriptionComponent } from './auth/subscription/subscription.component';
import { SubscriptionPlanComponent } from './auth/subscription/subscription-plan/subscription-plan.component';
import { SubscriptionConfirmComponent } from './auth/subscription/subscription-confirm/subscription-confirm.component';
import { SubscriptionCardComponent } from './auth/subscription/subscription-card/subscription-card.component';
import { SubscriptionAmountComponent } from './auth/subscription/subscription-amount/subscription-amount.component';
import { PlanDetailComponent } from './auth/subscription/subscription-amount/plan-detail/plan-detail.component';
import { OwlModule } from 'ngx-owl-carousel';
import { FrontendBufferComponent } from './frontend-buffer/frontend-buffer.component';
import { SpinnerLoaderComponent } from './spinner-loader/spinner-loader.component';
import { VirtualToursVideoComponent } from './space-details/virtual-tours-video/virtual-tours-video.component';
import { SpaceDetailsIframeComponent } from './space-details-iframe/space-details-iframe.component';
import { VerifyCollaboratorComponent } from './auth/verify-collaborator/verify-collaborator.component';
import { LoginCollaboratorsComponent } from './auth/verify-collaborator/login-collaborators/login-collaborators.component';
import { RegisterCollaboratorComponent } from './auth/verify-collaborator/register-collaborator/register-collaborator.component';
const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  wheelPropagation: true
};

export function createTranslateLoader(httpBackendClient: HttpBackendClient) {
  return new TranslateHttpLoader(httpBackendClient, environment.languageUrl + '/', '.json');
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    RegisterComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    HeaderComponent,
    FooterComponent,
    LoaderComponent,
    VerifyComponent,
    NotFoundComponent,
    TopPageComponent,
    MainFooterComponent,
    ScenesComponent,
    ContactComponent,
    FaqComponent,
    ProductionComponent,
    LanguageComponent,
    LanguageRedirectComponent,
    SafeUrl,
    SafeHtml,
    NewsListComponent,
    NewsDetailComponent,
    PageLoaderComponent,
    PaymentComponent,
    AutofocusDirective,
    TooltipDirective,
    SpaceDetailsComponent,
    VirtualToursComponent,
    PageDetailsComponent,
    PageoverlayLoaderComponent,
    PlanComponent,
    CardComponent,
    ConfirmComponent,
    AmountComponent,
    PlanDetailsComponent,
    SubscriptionComponent,
    SubscriptionPlanComponent,
    SubscriptionConfirmComponent,
    SubscriptionCardComponent,
    SubscriptionAmountComponent,
    PlanDetailComponent,
    SpinnerLoaderComponent,
    FrontendBufferComponent,
    VirtualToursVideoComponent,
    SpaceDetailsIframeComponent,
    TruncatePipe,
    EscapeHtmlPipe,
    VerifyCollaboratorComponent,
    LoginCollaboratorsComponent,
    RegisterCollaboratorComponent,
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    MemberModule,
    AppRoutingModule,
    ApiModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    NgxPaginationModule,
    PerfectScrollbarModule,
    OwlModule,
    ShareButtonsModule,
    FontAwesomeModule,
    SharedModule,
    TranslateModule.forRoot({
        loader: {
            provide: TranslateLoader,
            useFactory: createTranslateLoader,
            deps: [HttpBackendClient]
        }
    })
  ],
  providers: [
    {
      provide: BASE_PATH,
      useValue: environment.apiUrl
    },
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG
    },
    AuthService,
    AuthGuardService,
    HelperService,
    RedirectGuard,
    ScrollHelper,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    SpaceService,
    PaymentService,
    WorkflowService,
    WorkflowGuard,
    SubscriptionWorkflowGuard,
    SubscribeGuard,
    SubscriptionStatusGuard,
    HttpBackendClient
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
